import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import ShareBar from './components/ShareBar';
import { videos } from '@/mocks/videos';
import { categories } from '@/mocks/categories';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const video = videos.find((v) => v.id === id);

  if (!video) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <main className="flex-1 flex flex-col items-center justify-center px-4 py-24 text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950">未找到该视频</h1>
          <p className="mt-3 text-sm text-foreground-500">内容可能已下架或链接有误，返回首页继续探索吧。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const related = videos.filter((v) => v.category === video.category && v.id !== video.id).slice(0, 4);
  const category = categories.find((c) => c.type === video.category);

  const infoRows = [
    { label: '类型', value: video.categoryLabel },
    { label: '题材', value: video.genre },
    { label: '地区', value: video.region },
    { label: '年份', value: String(video.year) },
  ];

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* 顶部主视觉 */}
        <section className="relative w-full overflow-hidden bg-foreground-950">
          <img
            src={video.cover}
            alt={`${video.title} 背景`}
            className="absolute inset-0 w-full h-full object-cover object-top opacity-40 blur-sm"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/80 to-foreground-950/60"></div>
          <div className="relative z-10 max-w-[1440px] mx-auto px-4 md:px-8 py-8 md:py-12">
            <nav className="flex items-center gap-2 text-sm text-background-200/80 mb-5 flex-wrap">
              <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link
                to={`/category/${video.category}`}
                className="hover:text-primary-400 transition-colors cursor-pointer"
              >
                {video.categoryLabel}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-background-50">{video.title}</span>
            </nav>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-semibold">
                {video.categoryLabel}
              </span>
              <span className="px-2.5 py-1 rounded-md bg-accent-500 text-foreground-950 text-xs font-bold flex items-center gap-1">
                <i className="ri-star-fill text-[10px]"></i>
                {video.rating.toFixed(1)}
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-background-50">
              {video.title}
            </h1>
            <p className="mt-3 text-sm md:text-base text-background-200/85 max-w-3xl">
              {video.genre} · {video.region} · {video.year}
            </p>
          </div>
        </section>

        <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* 播放演示区 */}
            <div className="lg:col-span-2">
              <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-foreground-950 flex flex-col items-center justify-center text-center px-6">
                <span className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/20 text-primary-400 mb-5">
                  <i className="ri-play-circle-line text-4xl md:text-5xl"></i>
                </span>
                <p className="font-heading text-base md:text-lg font-bold text-background-50">演示模式</p>
                <p className="mt-2 text-sm text-background-200/70 max-w-md">
                  当前为前端演示区域，真实视频播放接口待接入，敬请期待完整播放体验。
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-background-50/10 text-background-200/80 text-xs">
                  <i className="ri-information-line"></i>
                  播放功能开发中
                </span>
              </div>

              <div className="mt-6 rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">剧情简介</h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed select-text">
                  {video.summary}
                </p>
                <div className="flex flex-wrap gap-2 mt-5">
                  {video.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* 信息面板 */}
            <div className="lg:col-span-1">
              <div className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">作品信息</h2>
                <dl className="flex flex-col gap-3">
                  {infoRows.map((row) => (
                    <div key={row.label} className="flex items-center justify-between text-sm">
                      <dt className="text-foreground-500">{row.label}</dt>
                      <dd className="text-foreground-900 font-medium">{row.value}</dd>
                    </div>
                  ))}
                  <div className="flex items-center justify-between text-sm">
                    <dt className="text-foreground-500">评分</dt>
                    <dd className="text-primary-600 font-bold">{video.rating.toFixed(1)} 分</dd>
                  </div>
                </dl>

                <div className="mt-6 pt-5 border-t border-background-200">
                  <p className="text-sm font-medium text-foreground-800 mb-3 flex items-center gap-2">
                    <i className="ri-share-forward-line text-primary-500"></i>
                    分享给朋友
                  </p>
                  <ShareBar title={video.title} />
                </div>

                <Link
                  to={`/category/${video.category}`}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-background-100 text-foreground-800 text-sm font-medium hover:bg-primary-500 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{category ? category.label : '分类'}频道
                </Link>
              </div>
            </div>
          </div>

          {/* 相关推荐 */}
          {related.length > 0 && (
            <section className="mt-12">
              <div className="flex items-end justify-between mb-5">
                <h2 className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950 flex items-center gap-2">
                  <span className="w-1 h-6 rounded-full bg-primary-500"></span>
                  相关推荐
                </h2>
                <Link
                  to={`/category/${video.category}`}
                  className="flex items-center gap-1 text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  更多{video.categoryLabel}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {related.map((item) => (
                  <VideoCard key={item.id} video={item} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}